<!--
	SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
	SPDX-License-Identifier: AGPL-3.0-or-later
-->
<template>
	<NcModal :show.sync="isOpen">
		<div :class="$style.wrapper">
			<DetailsPage
				v-if="activeEntry"
				:entry="activeEntry"
				@back="activeEntry = undefined" />
			<OverviewPage
				v-else
				:category-name="categoryName"
				:entries="entries"
				@select-entry="onSelectEntry" />
		</div>
	</NcModal>
</template>

<script setup lang="ts">
import type { ISetupCheck } from '../../types'

import { computed, ref, watch } from 'vue'

import NcModal from '@nextcloud/vue/dist/Components/NcModal.js'
import DetailsPage from './DetailsPage.vue'
import OverviewPage from './OverviewPage.vue'

const props = defineProps<{
	categoryName: string
	currentEntry?: ISetupCheck
	entries: ISetupCheck[]
	open: boolean
}>()

const emit = defineEmits<{
	(e: 'update:open', v: boolean): void
}>()

const isOpen = computed({
	get: () => props.open,
	set: (open: boolean) => emit('update:open', open),
})

const activeEntry = ref(props.currentEntry)

watch([props], () => {
	activeEntry.value = props.currentEntry
})

const onSelectEntry = (entry: ISetupCheck) => {
	activeEntry.value = entry
}
</script>

<style module lang="scss">
.wrapper {
	display: flex;
	box-sizing: border-box;
	// handle height to prevent jumping content when switching between details and overview
	--modal-height: min(calc(100vh - 100px), 800px);
	height: var(--modal-height);
	max-height: var(--modal-height);
	width: 100%;
	max-width: 100%;
	padding: 22px;
	padding-inline-end: 0; // no padding to prevent floating scroll bar
}

@media only screen and (max-width: 512px) {
	.wrapper {
		--modal-height: 100%;
	}
}
</style>
